import { LinkOutlined, SaveOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-components';
import { useMutation, useQuery } from '@apollo/client/react';
import { App, Button, Form, Input, Select, Switch } from 'antd';
import { useForm } from 'antd/es/form/Form';
import type React from 'react';
import { LoadingIndicator } from '../../components/LoadingIndicator';
import { MainContent } from '../../components/MainContent';
import { type SystemStatusInput, SystemStatusType } from '../../graphql/generated/graphql';
import { translateSystemStatusType } from '../../utils/enumHelpers';
import { SYSTEM_STATUS_QUERY, UPDATE_SYSTEM_STATUS_MUTATION } from './SystemStatus.gql';

export const SystemStatus: React.FC = () => {
  const [form] = useForm<SystemStatusInput>();
  const [updateSystemStatusMutation, { loading: updateLoading }] = useMutation(UPDATE_SYSTEM_STATUS_MUTATION);
  const { message } = App.useApp();

  const save = async (values: SystemStatusInput) => {
    try {
      await updateSystemStatusMutation({
        variables: {
          input: { ...values, link: values.link.trim(), content: values.content.trim() },
        },
      });

      message.success('System Statusmeldung wurde aktualisiert');
    } catch {
      message.error('System Statusmeldung konnte nicht aktualisiert werden');
    }
  };

  const { data, loading } = useQuery(SYSTEM_STATUS_QUERY);

  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <MainContent size="medium">
      <PageHeader title="System Statusmeldung" style={{ padding: 0, paddingBottom: 'inherit' }} />
      <Form
        labelCol={{ xs: 3, xl: 4 }}
        colon={false}
        labelAlign="left"
        form={form}
        onFinish={save}
        initialValues={{
          type: data?.systemStatus?.type ?? SystemStatusType.INFO,
          active: data?.systemStatus?.active ?? false,
          link: data?.systemStatus?.link ?? '',
          content: data?.systemStatus?.content ?? '',
          heading: data?.systemStatus?.heading ?? '',
        }}
      >
        <Form.Item name="active" label="Aktiv">
          <Switch />
        </Form.Item>
        <Form.Item name="heading" label="Überschrift">
          <Input autoFocus />
        </Form.Item>
        <Form.Item name="content" label="Inhalt">
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item name="link" label="Link" rules={[{ type: 'url' }]}>
          <Input prefix={<LinkOutlined />} />
        </Form.Item>
        <Form.Item name="type" label="Typ">
          <Select>
            {Object.keys(SystemStatusType).map(it => (
              <Select.Option key={it} value={it}>
                {translateSystemStatusType(it)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Button
          htmlType="submit"
          type="primary"
          disabled={updateLoading || form.getFieldsError().some(({ errors }) => errors.length)}
          icon={<SaveOutlined />}
        >
          Speichern
        </Button>
      </Form>
    </MainContent>
  );
};
